import React, { useState } from 'react';
import {
  Box as BoxIcon,
  Circle,
  CircleDot,
  Cone,
  Cuboid,
  Cylinder,
  Disc,
  Donut,
  Gem,
  Hexagon,
  Shapes,
  Triangle
} from 'lucide-react';
import { ShapeGenerators } from '../utils/geometry';

const SHAPES = [
  { id: 'box', name: 'Box', icon: BoxIcon, params: { width: 30, height: 20, depth: 30 } },
  { id: 'sphere', name: 'Sphere', icon: Circle, params: { radius: 15 } },
  { id: 'cylinder', name: 'Cylinder', icon: Cylinder, params: { radiusTop: 14, radiusBottom: 14, height: 30 } },
  { id: 'cone', name: 'Cone', icon: Cone, params: { radius: 15, height: 32 } },
  { id: 'torus', name: 'Torus', icon: Donut, params: { radius: 16, tube: 4 } },
  { id: 'plane', name: 'Plane', icon: Disc, params: { width: 40, height: 40 } },
  { id: 'circle', name: 'Circle', icon: CircleDot, params: { radius: 18 } },
  { id: 'ring', name: 'Ring', icon: CircleDot, params: { innerRadius: 10, outerRadius: 18 } },
  { id: 'tetrahedron', name: 'Tetrahedron', icon: Triangle, params: { radius: 18 } },
  { id: 'octahedron', name: 'Octahedron', icon: Gem, params: { radius: 18 } },
  { id: 'dodecahedron', name: 'Dodecahedron', icon: Hexagon, params: { radius: 18 } },
  { id: 'icosahedron', name: 'Icosahedron', icon: Cuboid, params: { radius: 18 } },
  { id: 'torusKnot', name: 'Knot', icon: Shapes, params: { radius: 16, tube: 4 } }
];

export default function ShapeLibrary({ onAddShape, onError }) {
  const [selectedShape, setSelectedShape] = useState(null);
  const [shapeParams, setShapeParams] = useState({});
  const [shapeColor, setShapeColor] = useState('#f59e0b');

  const handleShapeClick = (shape) => {
    setSelectedShape(shape);
    setShapeParams(shape.params);
  };

  const handleParamChange = (key, value) => {
    setShapeParams(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  const handleAddShape = () => {
    if (!selectedShape) return;

    try {
      const color = parseInt(shapeColor.replace('#', '0x'));
      const result = ShapeGenerators[selectedShape.id]({
        ...shapeParams,
        color
      });
      result.mesh.name = selectedShape.name;
      result.mesh.userData = {
        ...result.mesh.userData,
        type: selectedShape.name,
        name: selectedShape.name
      };

      onAddShape(result);
      setSelectedShape(null);
      setShapeParams({});
    } catch (error) {
      onError(error);
    }
  };

  return (
    <div className="panel">
      <div className="panel-header">Primitives</div>
      
      <div className="shape-grid mb-4">
        {SHAPES.map(shape => {
          const Icon = shape.icon;
          return (
            <button
              key={shape.id}
              className={`shape-card ${selectedShape?.id === shape.id ? 'selected' : ''}`}
              onClick={() => handleShapeClick(shape)}
            >
              <Icon size={22} />
              <span>{shape.name}</span>
            </button>
          );
        })}
      </div>

      {selectedShape && (
        <div className="space-y-3 fade-in">
          <div className="text-sm font-medium text-neutral-200">
            Configure {selectedShape.name}
          </div>

          <div className="space-y-2">
            {Object.entries(shapeParams).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between gap-2">
                <label className="text-xs text-neutral-400 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.01"
                  value={value}
                  onChange={(e) => handleParamChange(key, e.target.value)}
                  className="input w-24"
                />
              </div>
            ))}

            <div className="flex items-center justify-between gap-2">
              <label className="text-xs text-neutral-400">Color</label>
              <input
                type="color"
                value={shapeColor}
                onChange={(e) => setShapeColor(e.target.value)}
                className="w-20 h-8"
              />
            </div>
          </div>

          <button
            onClick={handleAddShape}
            className="btn btn-primary w-full"
          >
            Add to Workplane
          </button>
        </div>
      )}
    </div>
  );
}

